import { konvaSettings } from '../konvaSettings';

import { Bounding } from '../../../core/models/bounding';
import { Equipment } from '../equipment';
import { Figure } from './figure';
import { Point } from '../../../core/models/point';
import { Range } from '../../../core/models/range';

import { Utils } from '../../../core/services/utils.service';

export class Cylinder implements Figure {
	type: string = 'cylinder';
	begin: Point;
	width: number;
	height: number;
	depth: number;
	diameter: number;

	// TODO delete on backend
	empty: any = { front: false, top: false };
	fillColor: string = konvaSettings.fillGreyColor;
	strokeColor: string = konvaSettings.fillDarkColor;

	set beginSetter(value: Point) {
		this.begin = {
			x: Utils.round(value.x),
			y: Utils.round(value.y),
			z: Utils.round(value.z),
		};
	}

	get coordinates(): Array<Point> {
		const boundingX: Bounding = this.getBoundingX();
		const boundingY: Bounding = this.getBoundingY();
		const boundingZ: Bounding = this.getBoundingZ();
		return [
			{ x: boundingX.min, y: boundingY.min, z: boundingZ.max },
			{ x: boundingX.max, y: boundingY.min, z: boundingZ.min },
			{ x: boundingX.max, y: boundingY.max, z: boundingZ.max },
			{ x: boundingX.min, y: boundingY.max, z: boundingZ.min },
			{ x: boundingX.min, y: boundingY.min, z: boundingZ.max }
		];
	}

	get savedParams(): Object {
		return {
			type: this.type,
			begin: this.begin,
			width: this.width,
			height: this.height,
			depth: this.depth,
			diameter: this.diameter,
			empty: this.empty,
			fillColor: this.fillColor,
			strokeColor: this.strokeColor
		}
	}

	get square(): number {
		const length: number = this.width || this.height || this.depth;
		return Utils.round(length * this.diameter);
	}

	static returnClearCacheObject() {
		return {
			front: {},
			top: {},
			side: {}
		};
	}

	constructor(params: any) {
		this.begin = params.begin;
		this.width = params.width ?? 0;
		this.height = params.height ?? 0;
		this.depth = params.depth ?? 0;
		this.diameter = params.diameter;
	}

	public copy(): Cylinder {
		return new Cylinder({
			begin: Object.assign({}, this.begin),
			width: this.width,
			height: this.height,
			depth: this.depth,
			diameter: this.diameter
		});
	}

	public drag(x?: number, y?: number, z?: number): void {
		if (x) this.begin.x = Utils.round(this.begin.x + x);
		if (y) this.begin.y = Utils.round(this.begin.y + y);
		if (z) this.begin.z = Utils.round(this.begin.z + z);
	}

	public getBoundingX(): Bounding {
		if (this.width) return { min: this.begin.x, max: Utils.round(this.begin.x + this.width) };

		return {
			min: Utils.round(this.begin.x - this.diameter / 2),
			max: Utils.round(this.begin.x + this.diameter / 2)
		}
	}

	public getBoundingY(): Bounding {
		if (this.height) return { min: this.begin.y, max: Utils.round(this.begin.y + this.height) };

		return {
			min: Utils.round(this.begin.y - this.diameter / 2),
			max: Utils.round(this.begin.y + this.diameter / 2)
		}
	}

	public getBoundingZ(): Bounding {
		if (this.depth) return { min: this.begin.z, max: Utils.round(this.begin.z + this.depth) };

		return {
			min: Utils.round(this.begin.z - this.diameter / 2),
			max: Utils.round(this.begin.z + this.diameter / 2)
		}
	}

	public getBoundingZBetween(compareSize: Bounding): Bounding {
		return compareSize;
	}

	public getLines(representation: string, equipment: Equipment): Array<Array<number>> {
		switch (representation) {
			case 'front': {
				const height: number = equipment.Height;
				const boundingX: Bounding = this.getBoundingX();
				const boundingY: Bounding = this.getBoundingY();

				return [
					[boundingX.min, height - boundingY.max, boundingX.max, height - boundingY.max],
					[boundingX.max, height - boundingY.max, boundingX.max, height - boundingY.min],
					[boundingX.max, height - boundingY.min, boundingX.min, height - boundingY.min],
					[boundingX.min, height - boundingY.min, boundingX.min, height - boundingY.max]
				];
			}
			case 'top': {
				const boundingX: Bounding = this.getBoundingX();
				const boundingZ: Bounding = this.getBoundingZ();

				return [
					[boundingX.min, boundingZ.max, boundingX.max, boundingZ.max],
					[boundingX.max, boundingZ.max, boundingX.max, boundingZ.min],
					[boundingX.max, boundingZ.min, boundingX.min, boundingZ.min],
					[boundingX.min, boundingZ.min, boundingX.min, boundingZ.max]
				];
			}
			case 'side': {
				const height: number = equipment.Height;
				const boundingY: Bounding = this.getBoundingY();
				const boundingZ: Bounding = this.getBoundingZ();

				return [
					[boundingZ.min, height - boundingY.max, boundingZ.max, height - boundingY.max],
					[boundingZ.max, height - boundingY.max, boundingZ.max, height - boundingY.min],
					[boundingZ.max, height - boundingY.min, boundingZ.min, height - boundingY.min],
					[boundingZ.min, height - boundingY.min, boundingZ.min, height - boundingY.max]
				];
			}
		}
	}

	public getObjectHash(kwargs: any): string {
		return '';
	}

	public inRange(range: Range): boolean {
		const boundingX: Bounding = this.getBoundingX();

		return !(range.to <= boundingX.min || range.from >= boundingX.max);
	}

	public isPointOn(x?: number, y?: number, z?: number): boolean {
		if (x != null) {
			const boundingX: Bounding = this.getBoundingX();
			x = Utils.round(x);
			if (x < boundingX.min || x > boundingX.max) return false;
		}
		if (y != null) {
			const boundingY: Bounding = this.getBoundingY();
			y = Utils.round(y);
			if (y < boundingY.min || y > boundingY.max) return false;
		}
		if (z != null) {
			const boundingZ: Bounding = this.getBoundingZ();
			z = Utils.round(z);
			if (z < boundingZ.min || z > boundingZ.max) return false;
		}

		return true;
	}

	public update(params: any): void {
		Object.keys(params).forEach((param: string) => {
			if (this[param] != undefined) this[param] = params[param];
		});
	}
}